import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, RefreshCw, ShieldCheck, Loader2, CheckCircle2,
  ArrowDownToLine, Search,
} from "lucide-react";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import { getBackendUrl } from "@/lib/backend";

const API = `${getBackendUrl()}/api`;

/**
 * AdminBillingLeaks
 * -----------------
 * Surfaces `/api/admin/billing/audit-leaks`. Shows captured payments
 * where the implied per-credit rate fell below the ₹{floor} floor — i.e.
 * the customer received credits that mathematically can never be
 * legit under our current pack catalog.
 *
 * One-click clawback is gated by a destructive confirm modal because it
 * subtracts credits from real user wallets and writes an audit ledger.
 */
export default function AdminBillingLeaks({ authHeader, onUnauthorized }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [clawbackOpen, setClawbackOpen] = useState(false);
  const [clawingBack, setClawingBack] = useState(false);

  const headers = useMemo(
    () => (authHeader ? { Authorization: authHeader } : {}),
    [authHeader],
  );

  const fetchLeaks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/billing/audit-leaks`, {
        headers, params: { limit: 1000 },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to load leak audit.");
    } finally {
      setLoading(false);
    }
  }, [headers, onUnauthorized]);

  useEffect(() => { fetchLeaks(); }, [fetchLeaks]);

  const runClawback = async () => {
    setClawingBack(true);
    try {
      const r = await axios.get(`${API}/admin/billing/audit-leaks`, {
        headers,
        params: { limit: 1000, clawback: "true" },
      });
      setData(r.data);
      const n = r.data?.clawed_back_count || 0;
      toast.success(`Clawback complete · ${n} wallet${n === 1 ? "" : "s"} adjusted.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clawback failed.");
    } finally {
      setClawingBack(false);
      setClawbackOpen(false);
    }
  };

  const leaks = data?.leaks || [];
  const visibleLeaks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leaks;
    return leaks.filter((l) =>
      [l.order_id, l.user_id, l.user_email, l.pack_id, l.coupon_code]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [leaks, query]);

  const pendingClawbackCount = leaks.filter((l) => !l.clawback_applied).length;
  const totalLeakInr = data?.total_leak_inr ?? 0;
  const totalOvergranted = data?.total_credits_overgranted_estimate ?? 0;

  return (
    <div data-testid="admin-billing-leaks" className="space-y-6">
      {/* Header strip */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-rose-300/85">
            <AlertTriangle className="w-3.5 h-3.5" />
            Forensic billing audit
          </div>
          <h2 className="mt-1 font-display text-2xl text-white tracking-tight">
            Suspicious payments
          </h2>
          <p className="mt-1 text-xs text-white/55 max-w-xl">
            Captured Razorpay payments where credits granted × ₹
            {data?.floor_rate_inr_per_credit ?? 7.5}/credit floor exceeded what
            the customer actually paid. These are mathematically impossible
            under the current pack catalog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLeaks}
            disabled={loading}
            data-testid="leaks-refresh-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
          {pendingClawbackCount > 0 && (
            <button
              onClick={() => setClawbackOpen(true)}
              disabled={clawingBack}
              data-testid="leaks-clawback-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-rose-500/90 text-white hover:bg-rose-500 font-medium transition disabled:opacity-40"
            >
              <ArrowDownToLine className="w-3.5 h-3.5" />
              Claw back {pendingClawbackCount}
            </button>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          accent={leaks.length > 0}
          label="Leak rows"
          value={leaks.length}
          testId="leaks-kpi-count"
        />
        <KpiTile
          accent={totalLeakInr > 0}
          label="Total leak"
          value={`₹${Number(totalLeakInr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
          testId="leaks-kpi-total-inr"
        />
        <KpiTile
          accent={totalOvergranted > 0}
          label="Credits over-granted"
          value={Number(totalOvergranted).toLocaleString("en-IN")}
          testId="leaks-kpi-over-credits"
        />
        <KpiTile
          accent={false}
          label="Scanned"
          value={Number(data?.scanned || 0).toLocaleString("en-IN")}
          testId="leaks-kpi-scanned"
        />
      </div>

      {/* Empty state */}
      {!loading && leaks.length === 0 && (
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04] p-8 grid place-items-center text-center">
          <ShieldCheck className="w-7 h-7 text-emerald-300 mb-2" />
          <div className="font-display text-lg text-white">No leaks detected</div>
          <div className="text-xs text-white/55 mt-1">
            Every captured payment is consistent with the catalog.
          </div>
        </div>
      )}

      {/* Search */}
      {leaks.length > 0 && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Search by order id, user, pack, coupon…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="leaks-search-input"
            className="w-full bg-[#141414] border border-white/8 rounded-xl pl-9 pr-3 py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/40"
          />
        </div>
      )}

      {/* Table */}
      {visibleLeaks.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-white/8 bg-[#0E0E0E]" data-testid="leaks-table-wrap">
          <table className="w-full text-xs">
            <thead className="text-left text-white/45 border-b border-white/8">
              <tr>
                <th className="px-3 py-2.5 font-medium">Order</th>
                <th className="px-3 py-2.5 font-medium">User Email</th>
                <th className="px-3 py-2.5 font-medium">User ID</th>
                <th className="px-3 py-2.5 font-medium">Pack</th>
                <th className="px-3 py-2.5 font-medium">Coupon</th>
                <th className="px-3 py-2.5 font-medium text-right">Paid</th>
                <th className="px-3 py-2.5 font-medium text-right">Credits</th>
                <th className="px-3 py-2.5 font-medium text-right">₹/credit</th>
                <th className="px-3 py-2.5 font-medium text-right">Leak ₹</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleLeaks.map((l) => (
                <tr
                  key={l.order_id}
                  data-testid={`leak-row-${l.order_id}`}
                  className={`border-t border-white/5 ${
                    l.clawback_applied ? "opacity-50" : ""
                  } hover:bg-white/[0.02]`}
                >
                  <td className="px-3 py-2 font-mono text-[11px] text-white/70">{l.order_id?.slice(-10) || "—"}</td>
                  <td className="px-3 py-2 text-white/80" title={l.user_email}>{l.user_email || "(unknown)"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/65" title={l.user_id}>{l.user_id?.slice(-8) || "—"}</td>
                  <td className="px-3 py-2 text-white/80">{l.pack_id || "—"}</td>
                  <td className="px-3 py-2 text-[#D4AF37]/80">{l.coupon_code || "—"}</td>
                  <td className="px-3 py-2 text-right text-white/80">₹{Number(l.amount_paid_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-right text-white/80">{Number(l.credits_granted).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right text-rose-300">₹{Number(l.implied_per_credit_rate_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-right font-medium text-rose-300">₹{Number(l.leak_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2">
                    {l.clawback_applied ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300/80">
                        <CheckCircle2 className="w-3 h-3" /> clawed back
                      </span>
                    ) : (
                      <span className="text-rose-300/80">pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LuxuryConfirmModal
        open={clawbackOpen}
        onOpenChange={setClawbackOpen}
        title={`Claw back ${pendingClawbackCount} payment${pendingClawbackCount === 1 ? "" : "s"}?`}
        description={
          `This will subtract the proportional over-credit from each affected ` +
          `customer's wallet and record a 'billing_leak_clawback' ledger entry. ` +
          `Wallets are clamped at zero — we won't push anyone negative. The ` +
          `action is idempotent (won't double-debit) but you cannot undo a ` +
          `successful clawback without manually crediting it back.`
        }
        confirmLabel={clawingBack ? "Working…" : "Yes, claw back"}
        destructive
        onConfirm={runClawback}
      />
    </div>
  );
}

function KpiTile({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 ${
        accent
          ? "border-rose-400/30 bg-rose-400/[0.04]"
          : "border-white/8 bg-[#0E0E0E]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
      <div className={`mt-1.5 font-display text-2xl tracking-tight ${accent ? "text-rose-300" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
